import React, { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_PATHS = {
    adventure: "adventures",
    profile: "profiles",
    marketplace: "marketplace",
};

const KIND_COPY = {
    adventure: {
        heading: "Share this adventure",
        blurb: "Your link renders a rich preview everywhere — X, Reddit, Discord, iMessage, WhatsApp. Great for r/CampingandHiking drops.",
        campaign: "share_adventure",
    },
    profile: {
        heading: "Share this profile",
        blurb: "Recruit followers or introduce your crew. Any paste into social gets a rich card with the cover photo and stats.",
        campaign: "share_profile",
    },
    marketplace: {
        heading: "Share this listing",
        blurb: "Drive buyers from outside RealWorld Outdoors. Posts to Facebook Marketplace groups, Reddit gear threads, or Discord sale channels pick up the price + photo automatically.",
        campaign: "share_listing",
    },
};

function withUtm(baseUrl, source, medium, campaign) {
    if (!baseUrl) return "";
    const sep = baseUrl.includes("?") ? "&" : "?";
    return `${baseUrl}${sep}utm_source=${source}&utm_medium=${medium}&utm_campaign=${campaign}`;
}

/**
 * ShareModal — generic social share for adventures, profiles and marketplace listings.
 * The canonical share URL hits the backend OG endpoint so scrapers get rich cards;
 * real browsers are redirected to the React app. Each channel gets its own UTM
 * source so you can attribute traffic in analytics.
 */
export default function ShareModal({ open, onOpenChange, kind, id, title, byline, preview }) {
    const copy = KIND_COPY[kind] || KIND_COPY.adventure;
    const path = KIND_PATHS[kind] || "adventures";

    const baseShareUrl = useMemo(() => {
        if (!id || !BACKEND_URL) return "";
        return `${BACKEND_URL}/api/share/${path}/${id}`;
    }, [id, path]);

    const directUrl = withUtm(baseShareUrl, "direct", "link", copy.campaign);
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(directUrl);
            setCopied(true);
            toast.success("Link copied — paste it anywhere");
            setTimeout(() => setCopied(false), 1800);
        } catch {
            toast.error("Copy failed");
        }
    };

    if (!id) return null;

    const safeTitle = title || "RealWorld Outdoors";
    const tagline = `${safeTitle}${byline ? ` ${byline}` : ""}`;

    const targets = [
        {
            id: "x",
            label: "Post on X",
            bg: "bg-black text-white",
            href: (u) =>
                `https://twitter.com/intent/tweet?url=${encodeURIComponent(u)}&text=${encodeURIComponent(tagline)}`,
            utm: { source: "x", medium: "social" },
            icon: (
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
            ),
        },
        {
            id: "reddit",
            label: "Post to Reddit",
            bg: "bg-[#FF4500] text-white",
            href: (u) =>
                `https://www.reddit.com/submit?url=${encodeURIComponent(u)}&title=${encodeURIComponent(tagline)}`,
            utm: { source: "reddit", medium: "social" },
            icon: (
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                    <path d="M12 0a12 12 0 100 24 12 12 0 000-24zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 01-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 01.042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 014.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 01.14-.197.35.35 0 01.238-.042l2.906.617a1.214 1.214 0 011.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 00-.231.094.33.33 0 000 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 00.029-.463.33.33 0 00-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 00-.232-.095z" />
                </svg>
            ),
        },
        {
            id: "facebook",
            label: "Share on Facebook",
            bg: "bg-[#1877F2] text-white",
            href: (u) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}`,
            utm: { source: "facebook", medium: "social" },
            icon: (
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                    <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z" />
                </svg>
            ),
        },
        {
            id: "whatsapp",
            label: "Send on WhatsApp",
            bg: "bg-[#25D366] text-white",
            href: (u) =>
                `https://wa.me/?text=${encodeURIComponent(tagline)}%20${encodeURIComponent(u)}`,
            utm: { source: "whatsapp", medium: "messaging" },
            icon: (
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                </svg>
            ),
        },
    ];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-snowcap border-ridge-line max-w-md" data-testid="share-modal">
                <DialogHeader>
                    <DialogTitle className="font-display text-midnight-pine tracking-tight text-2xl">
                        {copy.heading}
                    </DialogTitle>
                </DialogHeader>

                <div className="mt-2">
                    <p className="text-sm text-slate-gray mb-4 leading-relaxed">{copy.blurb}</p>

                    <div className="flex gap-2 mb-5">
                        <input
                            readOnly
                            value={directUrl}
                            data-testid="share-url-input"
                            onFocus={(e) => e.target.select()}
                            className="flex-1 px-3 py-2.5 rounded-md bg-alpine-mist border border-ridge-line text-sm text-granite font-mono truncate focus:outline-none focus:border-ember-orange"
                        />
                        <button
                            onClick={handleCopy}
                            data-testid="share-copy-btn"
                            className="px-4 py-2.5 rounded-md bg-ember-orange text-white font-bold text-sm hover:bg-ember-dark active:scale-95 transition-all inline-flex items-center gap-1.5"
                        >
                            {copied ? <><Check className="w-4 h-4" /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}
                        </button>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        {targets.map((t) => {
                            const url = withUtm(baseShareUrl, t.utm.source, t.utm.medium, copy.campaign);
                            return (
                                <a
                                    key={t.id}
                                    href={t.href(url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`share-${t.id}-btn`}
                                    className={`${t.bg} inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-md font-bold text-sm hover:opacity-90 active:scale-95 transition-all`}
                                >
                                    {t.icon}
                                    {t.label}
                                </a>
                            );
                        })}
                    </div>

                    {preview && (
                        <div className="mt-5 pt-5 border-t border-ridge-line text-xs text-slate-gray">
                            <strong className="font-bold text-granite">Preview:</strong> {preview}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
